import { BulkUpdateProgressBanner } from "#src/batteries-included-components/Banners/BulkUpdateProgressBanner/BulkUpdateProgressBanner";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import {
  EstimationMethodDetailFilterConfigFilterType,
  EstimationMethodDetailViewConfigFilterType,
  EstimationMethodDetailViewFilterArea,
  EstimationMethodsTableFilterArea,
  EstimationMethodTableTitleDecorationFilterArea,
} from "#src/batteries-included-components/FilterAreas/EstimationMethodsFilterAreas/EstimationMethodsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink/RoutingLink";
import {
  ESTIMATION_METHODS_QUERY_KEY,
  useBulkUpdateEstimationMethodStatus,
} from "#src/components/hooks/adapters/useEstimationMethods";
import { EquipmentUpdateStatusBanner } from "#src/components/Organization/Facilities/Detail/Equipment/EquipmentUpdateStatusBanner";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { FlowUpdateStatusBanner } from "#src/routes/organization/flows/[flowId]/detail/FlowUpdateStatusBanner";
import { linkToEstimationMethodDetailPage } from "#utils/links";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  HeaderType,
  Pill,
} from "@validereinc/common-components";
import type { AssetTypeType, EstimationMethodType } from "@validereinc/domain";
import {
  EstimationMethodAdapter,
  EstimationMethodStatus,
  ResourceDefinitions,
  SortDirection,
} from "@validereinc/domain";
import { toTitleCaseString, yearMonthFromISO } from "@validereinc/utilities";
import React, { useState } from "react";

const ESTIMATION_METHOD_MODEL_KEYS = {
  Name: "name",
  Library: "analytics_library_id",
  "Reporting Group": "reporting_groups",
  Status: "status",
} as const;

const EstimationMethodsTab = ({
  entityId,
  entityType,
  onAdd,
  isWithinEntityDetail,
}: {
  entityId: string;
  entityType: AssetTypeType;
  onAdd: () => void;
  isWithinEntityDetail?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    viewConfigStorageKey,
    tableStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
    ],
    [`${entityType}${isWithinEntityDetail ? "-detail" : ""}-estimation-methods`]
  );

  const [filters] =
    useStickyState<EstimationMethodDetailFilterConfigFilterType>(
      {},
      filterConfigStorageKey
    );
  const [{ month }] =
    useStickyState<EstimationMethodDetailViewConfigFilterType>(
      {},
      viewConfigStorageKey
    );

  const [selectedEstimationMethods, setSelectedEstimationMethods] = useState<
    Record<string, EstimationMethodType>
  >({});
  const [isSelectionInverted, setIsSelectionInverted] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [
    isUpdateEstimationStatusDialogOpen,
    setIsUpdateEstimationStatusDialogOpen,
  ] = useState(false);

  const [tableState, setTableState] = useTableSortingAndPagination(
    {
      sortBy: ESTIMATION_METHOD_MODEL_KEYS.Name,
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const reqParams: Parameters<typeof EstimationMethodAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      entity_id: entityId,
      entity_type: entityType,
      ...filters,
    },
    meta: { ...(month?.from ? { period: yearMonthFromISO(month?.from) } : {}) },
  };

  const { data: estimationMethods, isLoading } = useQuery(
    [ESTIMATION_METHODS_QUERY_KEY, reqParams],
    () => EstimationMethodAdapter.getList(reqParams)
  );

  const [bulkUpdateEstimationMethodStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `estimation-methods-bulk-update${isWithinEntityDetail ? "-detail" : ""}-${entityType}`
  );

  const { handleBulkUpdateEstimationMethodStatus } =
    useBulkUpdateEstimationMethodStatus({
      jobKey: bulkUpdateEstimationMethodStatusJobStorageKey,
      // @ts-expect-error TODO: fix filter typing
      filters: reqParams.filters,
      selectedEstimationMethods,
      isSelectionInverted,
      setIsUpdating,
      invalidate: () => {
        queryClient.invalidateQueries({
          queryKey: [ESTIMATION_METHODS_QUERY_KEY, reqParams],
        });
      },
    });

  const addEstimationMethod = () => {
    onAdd();
  };

  const tableHeaders: Array<HeaderType<EstimationMethodType>> = [
    {
      label: "Name",
      key: ESTIMATION_METHOD_MODEL_KEYS.Name,
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={linkToEstimationMethodDetailPage(
            item.entity_type,
            item.entity_id,
            item.id
          )}
        >
          {item.name}
        </RoutingLink>
      ),
    },
    {
      label: "Status",
      key: "status",
      isSortable: true,
      renderComponent: ({ item }: { item: EstimationMethodType }) => {
        return (
          <Pill
            variant={
              item.status === EstimationMethodStatus.ACTIVE
                ? "success"
                : "default"
            }
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        );
      },
    },
    {
      label: "Reporting Scenario",
      key: ESTIMATION_METHOD_MODEL_KEYS["Reporting Group"],
      isSortable: false,
      renderComponent: ({
        item: { reporting_groups: reportingGroups },
      }: {
        item: EstimationMethodType;
      }) => reportingGroups?.map(({ name }) => name)?.join(", ") || "-",
    },
    {
      label: "Library",
      key: ESTIMATION_METHOD_MODEL_KEYS.Library,
      isSortable: true,
    },
  ];

  const tableActions = (
    <Button onClick={addEstimationMethod}>Add Estimation Method</Button>
  );

  return (
    <>
      <BulkUpdateProgressBanner
        jobStorageKey={bulkUpdateEstimationMethodStatusJobStorageKey}
        setSelectedItems={setSelectedEstimationMethods}
        setIsRunning={setIsUpdating}
        setIsSelectionInverted={setIsSelectionInverted}
        invalidate={() => {
          queryClient.invalidateQueries({
            queryKey: [ESTIMATION_METHODS_QUERY_KEY, reqParams],
          });
        }}
        subject={{
          singular: ResourceDefinitions.estimation_method.label.singular,
          plural: ResourceDefinitions.estimation_method.label.plural,
        }}
        action={(job) =>
          job?.input.updateBody.status === EstimationMethodStatus.INACTIVE
            ? "inactivate"
            : "activate"
        }
      />
      <FlowUpdateStatusBanner />
      <EquipmentUpdateStatusBanner />
      <EstimationMethodDetailViewFilterArea
        viewConfigStorageKey={viewConfigStorageKey}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Estimation Methods",
          actionRow: tableActions,
          titleDecorator: (
            <EstimationMethodTableTitleDecorationFilterArea
              filterConfigStorageKey={filterConfigStorageKey}
            />
          ),
        }}
        dataTableProps={{
          intent: "simplicity-first",
          items: estimationMethods?.data ?? [],
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            <IsFeatureAvailable
              key="delete"
              featureFlagQuery="core:estimation_methods"
              permissionQuery="estimation_methods:write"
              fallbackChildren={null}
            >
              <Button
                {...buttonProps}
                onClick={() => setIsUpdateEstimationStatusDialogOpen(true)}
                isLoading={isUpdating}
              >
                Update Status
              </Button>
            </IsFeatureAvailable>,
          ],
          selected: selectedEstimationMethods,
          isSelectionInverted: isSelectionInverted,
          onSelectionChange: (value, shouldInvertSelection) => {
            setSelectedEstimationMethods(value);
            setIsSelectionInverted(shouldInvertSelection ?? false);
          },
          headers: tableHeaders,
          isLoading,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: estimationMethods?.total_entries ?? 0,
          },
          onSortChange: setTableState,
          onPaginationChange: setTableState,
        }}
        filterComponent={
          <EstimationMethodsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            assetType={entityType}
          />
        }
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateEstimationStatusDialogOpen}
        onClose={() => {
          setIsUpdateEstimationStatusDialogOpen(false);
        }}
        onSubmit={handleBulkUpdateEstimationMethodStatus}
        itemCount={
          isSelectionInverted
            ? (estimationMethods?.total_entries ?? 0)
            : Object.keys(selectedEstimationMethods).length
        }
        subject={{
          singular: ResourceDefinitions.estimation_method.label.singular,
          plural: ResourceDefinitions.estimation_method.label.plural,
        }}
        warningMessage="This will delete all associated calculation results and inputs."
      />
    </>
  );
};

export default EstimationMethodsTab;
